import React from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { Star } from 'lucide-react';
import StartSubpageLayout from '../components/start/StartSubpageLayout';
import { startCard, startCardPadding, startCardPaddingCompact } from '../components/start/startPageTokens';

/**
 * Content constants live at module scope so other surfaces can render the same
 * story without duplicating copy — the welcome popup panels import these.
 */
export const CQC_HEADLINE = 'The #1 Remodeling Company in the Raleigh-Durham Area';

export const CQC_METRICS_LEFT = [
  { emoji: '🏆', value: '50+', label: 'Awards Won' },
  { emoji: '👥', value: '30+', label: 'Employees' },
  { emoji: '💵', value: '$20M+', label: 'Annual Revenue' },
];

export const CQC_METRICS_RIGHT = [
  { emoji: '⭐', value: '300+', label: 'Total Reviews' },
  { emoji: '🏗️', value: '1000+', label: 'Projects Completed' },
  { emoji: '⚠️', value: '100+', label: 'Mistakes Made' },
];

export const CQC_STORY_PARAGRAPHS = [
  'CQC Home, the leading design-build home remodeling company in the Research Triangle area, was founded by Ken Combs, who built the company from nothing through relentless hard work.',
  'Starting with small construction jobs, Ken steadily expanded the business, eventually incorporating design services to offer a comprehensive remodeling experience. Today, CQC Home is an industry leader, recognized with awards like "Best Contractor of Durham" and "Triangle Business Journal’s #1 Residential Remodeler" for its exceptional service and craftsmanship.',
  'The company’s success is driven by the strategic implementation of expert standard operating procedures (SOPs), key performance indicators (KPIs), and detailed job descriptions, all ensuring precision and efficiency in every project. With Daryl’s guidance, CQC Home has embraced technology innovation to streamline operations and enhance project management.',
  'Supported by industry-leading sales practices, effective networking, strong marketing, and sound financial strategies, CQC Home is on track to achieve $20 million in revenue in 2024. Now a management-run company, CQC Home continues to thrive, setting the standard for excellence in home remodeling.',
];

export const CQC_VIDEO_EMBED_URL =
  'https://www.youtube.com/embed/3JKq03WDVoo?si=niGltCQm0GxIC1Hw';

const StartCQCHome = ({ embedded = false }) => {
  const { darkMode } = useTheme();

  const bodyText = 'text-sm md:text-base leading-relaxed text-gray-600 dark:text-gray-300';
  const sectionTitle = 'text-lg font-semibold text-gray-800 dark:text-white';
  const metricEmoji = 'text-2xl leading-none mb-1.5';
  const metricValue = 'text-4xl font-bold mb-2 text-red-600 dark:text-red-400';
  const metricLabel = 'text-sm font-semibold text-gray-700 dark:text-gray-300';

  const mainContent = (
    <div className="space-y-6">
      <div className="flex items-center justify-center">
        <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-amber-100 dark:bg-amber-900/30">
          <Star className="w-5 h-5 text-amber-600 dark:text-amber-400 fill-current" />
          <span className="text-sm font-semibold text-amber-800 dark:text-amber-300">
            Fully Management Run
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
        <div className="lg:col-span-1 space-y-5">
          {CQC_METRICS_LEFT.map((m) => (
            <div key={m.label} className={`${startCard} ${startCardPaddingCompact} text-center`}>
              <div className={metricEmoji} aria-hidden="true">{m.emoji}</div>
              <div className={metricValue}>{m.value}</div>
              <div className={metricLabel}>{m.label}</div>
            </div>
          ))}
        </div>

        <div className="lg:col-span-3">
          <div
            className={`rounded-xl shadow-sm p-8 text-center h-full flex flex-col justify-center items-center border ${
              darkMode
                ? 'bg-gradient-to-br from-red-600 to-red-800 border-red-400/50'
                : 'bg-gradient-to-br from-red-500 to-red-700 border-red-400/50'
            }`}
          >
            <img src="/cqclogo.png" alt="CQC Home Logo" className="h-40 md:h-48 w-auto object-contain mb-6" />
            <div className="text-6xl md:text-7xl font-bold mb-4 text-white">1</div>
            <div className="text-2xl md:text-3xl font-bold text-white">Defined Proven Model & Systems</div>
          </div>
        </div>

        <div className="lg:col-span-1 space-y-5">
          {CQC_METRICS_RIGHT.map((m) => (
            <div key={m.label} className={`${startCard} ${startCardPaddingCompact} text-center`}>
              <div className={metricEmoji} aria-hidden="true">{m.emoji}</div>
              <div className={metricValue}>{m.value}</div>
              <div className={metricLabel}>{m.label}</div>
            </div>
          ))}
        </div>
      </div>

      <div className={`${startCard} ${startCardPadding}`}>
        {CQC_STORY_PARAGRAPHS.map((paragraph, i) => (
          <p
            key={i}
            className={`${bodyText} ${i < CQC_STORY_PARAGRAPHS.length - 1 ? 'mb-6' : ''}`}
          >
            {paragraph}
          </p>
        ))}
      </div>

      <div className={`${startCard} ${startCardPadding}`}>
        <h3 className={`${sectionTitle} mb-6 text-center`}>Learn More About CQC Home</h3>
        <div className="flex justify-center">
          <div className="w-full max-w-4xl">
            <div className="relative rounded-lg overflow-hidden" style={{ paddingBottom: '56.25%', height: 0 }}>
              <iframe
                width="560"
                height="315"
                src={CQC_VIDEO_EMBED_URL}
                title="YouTube video player"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
                className="absolute top-0 left-0 w-full h-full"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <StartSubpageLayout embedded={embedded} title={CQC_HEADLINE}>
      {mainContent}
    </StartSubpageLayout>
  );
};

export default StartCQCHome;
